/**
 * Productivity progress entries: credit only the % delta of estimate/completion minutes.
 * Example: 0→50% of 60 min = 30; 50→75% = 15.
 */

export const formatProductivityEntryLabel = (entry) => {
  if (entry?.displayLabel) return entry.displayLabel;
  const title = (entry?.taskTitle && String(entry.taskTitle).trim()) || 'Task';
  const from = Number(entry?.fromPercent) || 0;
  const to = Number(entry?.toPercent) || 0;
  return `${title} (completed from ${from}% to ${to}%)`;
};

/** Full one-shot completion (0→100%) is credited but not shown as a progress row. */
export const isDisplayableProductivityEntry = (entry) => {
  const from = Number(entry?.fromPercent) || 0;
  const to = Number(entry?.toPercent) || 0;
  return !(from === 0 && to >= 100);
};

export const resolveProgressPercent = (task) => {
  const raw = Number(task?.completionPercentage);
  if (Number.isFinite(raw)) return Math.max(0, Math.min(100, Math.round(raw)));
  const status = String(task?.status || '').toUpperCase();
  return status === 'COMPLETED' ? 100 : 0;
};

export const productivityBaseMinutes = (task) => {
  const estimate = Number(task?.estimateTimeInMinutes);
  if (Number.isFinite(estimate) && estimate > 0) return Math.round(estimate);
  const completion = Number(task?.completionTimeInMinutes);
  if (Number.isFinite(completion) && completion > 0) return Math.round(completion);
  return 0;
};

/** Time entered when finishing a task, not the estimate set at creation. */
export const completionSpentMinutes = (task) => {
  const spent = Number(task?.completionTimeInMinutes);
  if (Number.isFinite(spent) && spent > 0) return Math.round(spent);
  return 0;
};

export const buildProductivityEntry = ({
  task,
  fromPercent,
  toPercent,
  userId,
  dateStr,
  now = new Date(),
}) => {
  const from = Math.max(0, Math.min(100, Math.round(Number(fromPercent) || 0)));
  const to = Math.max(0, Math.min(100, Math.round(Number(toPercent) || 0)));
  if (to <= from || !task) return null;
  const spent = to >= 100 ? completionSpentMinutes(task) : 0;
  const baseMinutes = spent > 0 ? spent : productivityBaseMinutes(task);
  const delta = to - from;
  const creditedMinutes = spent > 0
    ? spent
    : (baseMinutes > 0 ? Math.max(0, Math.round((baseMinutes * delta) / 100)) : 0);
  const title = (task.title && String(task.title).trim())
    || (task.description ? String(task.description).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80) : '')
    || 'Task';
  const id = `pending-prod-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const iso = now.toISOString();
  const entry = {
    id,
    _id: id,
    userId: userId || null,
    taskId: task.id || task._id,
    taskTitle: title,
    date: dateStr || now.toLocaleDateString('en-CA'),
    fromPercent: from,
    toPercent: to,
    creditedMinutes,
    baseMinutes,
    source: 'TASK_PROGRESS',
    createdAt: iso,
    updatedAt: iso,
    syncStatus: 'pending',
  };
  entry.displayLabel = formatProductivityEntryLabel(entry);
  return entry;
};

export const sumCreditedMinutes = (entries = []) => (
  (Array.isArray(entries) ? entries : []).reduce(
    (sum, entry) => sum + Math.max(0, Number(entry?.creditedMinutes) || 0),
    0,
  )
);

/** Unique tasks with any credited progress or full completion on a day. */
export const countUniqueTasksWithProgress = (completedTasks = [], productivityEntries = []) => {
  const ids = new Set();
  (Array.isArray(completedTasks) ? completedTasks : []).forEach((task) => {
    if (task?.id) ids.add(String(task.id));
  });
  (Array.isArray(productivityEntries) ? productivityEntries : []).forEach((entry) => {
    if (entry?.taskId) ids.add(String(entry.taskId));
  });
  return ids.size;
};

/** Hide progress rows when the same task is already listed as fully completed. */
export const filterProductivityEntriesForCompletedList = (
  entries = [],
  completedTasks = [],
) => {
  const completedIds = new Set(
    (Array.isArray(completedTasks) ? completedTasks : [])
      .map((task) => task?.id)
      .filter(Boolean)
      .map(String),
  );
  return (Array.isArray(entries) ? entries : []).filter(
    (entry) => !entry?.taskId || !completedIds.has(String(entry.taskId)),
  );
};
